import type { PlayerIndex, SimStateSnapshot } from '@md/shared';
import { CONTROL_HINTS } from '../input/keyboard';

/** Bandeau d'information au-dessus du canvas + rappel des contrôles. */
export interface Hud {
  readonly element: HTMLElement;
  update(snapshot: SimStateSnapshot): void;
  /** Remplace l'aide aux contrôles (clavier partagé ↔ mode réseau). */
  setHints(hints: readonly { readonly who: string; readonly keys: string }[]): void;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) {
    node.className = className;
  }
  if (text !== undefined) {
    node.textContent = text;
  }
  return node;
}

export function formatTime(elapsedMs: number): string {
  const totalSeconds = Math.floor(elapsedMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const hundredths = Math.floor((elapsedMs % 1000) / 10);
  return `${minutes}:${String(seconds).padStart(2, '0')}.${String(hundredths).padStart(2, '0')}`;
}

/** Rendu textuel des étoiles obtenues (concept §4.2). */
export function formatStars(stars: number, max = 3): string {
  return '★'.repeat(Math.max(0, stars)) + '☆'.repeat(Math.max(0, max - stars));
}

interface EnergyGauge {
  readonly element: HTMLElement;
  update(energy: number, active: boolean, flags: Readonly<Record<string, number>>): void;
}

/** Jauge d'énergie d'un robot (concept §3.3). */
function createEnergyGauge(player: PlayerIndex, name: string): EnergyGauge {
  const element = el('div', `energy energy--p${player}`);
  element.dataset.testid = `energy-${player}`;
  const fill = el('span', 'energy__fill');
  const track = el('span', 'energy__track');
  track.append(fill);
  element.append(el('strong', 'energy__who', name), track);

  return {
    element,
    update(energy, active, flags): void {
      const percent = Math.round(Math.max(0, Math.min(1, energy)) * 100);
      fill.style.width = `${percent}%`;
      element.dataset.energy = String(percent);
      element.dataset.active = String(active);
      element.dataset.depleted = String(flags.depleted === 1);
      element.dataset.polarity = flags.polarity === -1 ? '-' : '+';
      element.dataset.clinging = String(flags.clinging === 1);
      element.dataset.degaussed = String(flags.degaussed === 1);
    },
  };
}

export function createHud(): Hud {
  const element = el('div', 'hud');

  const levelName = el('span', 'hud__level');
  levelName.dataset.testid = 'hud-level';

  const timer = el('span', 'hud__stat hud__stat--time');
  timer.dataset.testid = 'hud-time';

  const deaths = el('span', 'hud__stat');
  deaths.dataset.testid = 'hud-deaths';

  const bolts = el('span', 'hud__stat');
  bolts.dataset.testid = 'hud-bolts';

  const bar = el('div', 'hud__bar');
  bar.append(levelName, timer, bolts, deaths);

  const gauges = [createEnergyGauge(0, 'Polo'), createEnergyGauge(1, 'Mag')] as const;
  const gaugeRow = el('div', 'hud__gauges');
  gaugeRow.append(gauges[0].element, gauges[1].element);

  const hints = el('div', 'hud__hints');
  const setHints = (list: readonly { readonly who: string; readonly keys: string }[]): void => {
    hints.replaceChildren();
    for (const hint of list) {
      const row = el('span', 'hud__hint');
      row.append(el('strong', 'hud__hint-who', hint.who), document.createTextNode(` ${hint.keys}`));
      hints.append(row);
    }
  };
  setHints(CONTROL_HINTS);

  element.append(bar, gaugeRow, hints);

  return {
    element,
    setHints,
    update(snapshot: SimStateSnapshot): void {
      levelName.textContent = snapshot.levelName;
      timer.textContent = formatTime(snapshot.elapsedMs);
      deaths.textContent = `${snapshot.deaths} mort${snapshot.deaths > 1 ? 's' : ''}`;
      bolts.textContent = `🔩 ${snapshot.bolts.collected}/${snapshot.bolts.total}`;
      element.dataset.dead = String(snapshot.dead);
      for (const index of [0, 1] as const) {
        const player = snapshot.players[index];
        gauges[index].update(player.energy, player.fieldActive, player.flags);
      }
    },
  };
}
